import {
  Avatar,
  Box,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import { Variant } from "@mui/material/styles/createTypography";
import { stringAvatar } from "../../utils/avatar";
import { useNavigate } from "react-router-dom";
import { gray3 } from "../../theme";

interface Props {
  className?: string;
  paddingLeft?: string;
  opacity?: number;
  link?: string; // 💡 เปลี่ยนให้เป็น Optional (?) ถ้าไม่ส่งมา แปลว่าเป็นเมนูแม่ (มีลูก)
  label: string; // 💡 เปลี่ยนจาก text เป็น label
  labelVariant?: Variant; // 💡 เปลี่ยนจาก textVariant เป็น labelVariant
  iconType?: "img" | "Avatar"; // 💡 ซ้ายคือ Icon เสมอ
  icon?: string;
  iconAlt?: string;
  iconSize?: number;
  avatarSize?: number;
  onToggle?: () => void; // 💡 เปลี่ยนจาก onLeftImgClick ให้ชัดเจนว่าคือการกาง/หุบเมนู
  endIcon?: string; // 💡 ขวาคือ EndIcon
  endIconClassName?: string;
  endIconAlt?: string;
  endIconSize?: string;
  onEndIconClick?: () => void;
  showHoverBox?: boolean;
}

const SidebarItem = ({
  iconType = "img",
  iconSize = 24,
  avatarSize = 44,
  ...props
}: Props) => {
  const navigate = useNavigate();

  // 💡 ถ้ายอมส่ง link มาให้ไปหน้านั้น ถ้าไม่ส่งมา (เป็นเมนูแม่) ให้เรียก onToggle แทน
  const isMenuWithChildren = !props.link;
  const handleItemClick = isMenuWithChildren
    ? props.onToggle
    : () => navigate(props.link as string);

  const iconMargin = (avatarSize - iconSize) / 2;

  const renderLeftIcon = () => {
    if (!props.icon && iconType === "img") return null;

    if (iconType === "Avatar") {
      const avatarBase64Url = props.icon
        ? `data:image/png;base64,${props.icon}`
        : undefined;

      return (
        <Avatar
          src={avatarBase64Url}
          {...stringAvatar(props.label ?? "")}
          alt={props.iconAlt ?? "User Profile"}
          sx={{ bgcolor: props.icon ? gray3 : undefined }}
        />
      );
    }

    return (
      <img
        src={props.icon}
        alt={props.iconAlt}
        style={{ width: `${iconSize}px` }}
      />
    );
  };

  const leftIconElement = renderLeftIcon();

  return (
    <ListItemButton
      onClick={handleItemClick}
      className={props.className}
      sx={{
        display: "flex",
        alignItems: "center",
        flexShrink: 0,
        overflow: "clip",
        p: 0,
        mx: iconType === "Avatar" ? 0 : `${iconMargin}px`,
        textDecoration: "none",
        flexGrow: 0,
        opacity: props.opacity ?? 1,
        transition: ".3s",
        pl: props.paddingLeft,
      }}
    >
      {/* ขีดสีเขียวตอน Hover ด้านซ้าย */}
      {props.showHoverBox && (
        <Box
          className="sidebar-hover-box"
          sx={{ height: "16px", width: "4px" }}
        />
      )}

      {/* ไอคอนด้านซ้าย */}
      {leftIconElement && (
        <ListItemIcon
          sx={{ minWidth: "24px", pointerEvents: "none" }}
          onClick={props.onToggle}
        >
          {leftIconElement}
        </ListItemIcon>
      )}

      {/* ข้อความเมนู */}
      <ListItemText sx={{ m: 0, px: "10px", width: "100%" }}>
        <Typography
          noWrap
          color="common.white"
          variant={props.labelVariant ?? "h3"}
          textAlign="start"
        >
          {props.label}
        </Typography>
      </ListItemText>

      {/* ลูกศรชี้ลง/ขวา หรือ ไอคอนท้ายอื่นๆ */}
      {props.endIcon && (
        <Box
          component="img"
          className={props.endIconClassName}
          width={props.endIconSize}
          src={props.endIcon}
          alt={props.endIconAlt}
          onClick={(e) => {
            e.stopPropagation();
            if (props.onEndIconClick) props.onEndIconClick();
          }}
          sx={{ cursor: "pointer" }}
        />
      )}
    </ListItemButton>
  );
};

export default SidebarItem;
